import React, { useEffect, useRef, useState } from "react";
import "./header.css";
import Icon from "../ui/Icon";
import { nav, profile } from "../../data/site";
import { useActiveSection } from "../../lib/useActiveSection";

const NAV_IDS = nav.map((item) => item.id);
const isMac = typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform);

const Header = ({ theme, onToggleTheme, onOpenPalette }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const active = useActiveSection(NAV_IDS);

  useEffect(() => {
    if (!menuOpen) return undefined;
    const onKey = (e) => e.key === "Escape" && setMenuOpen(false);
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [menuOpen]);

  // Phone menu: lock page scroll while open, and drop the state when the layout
  // grows past the phone tier so aria-expanded never goes stale.
  useEffect(() => {
    document.body.style.overflow = menuOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [menuOpen]);

  useEffect(() => {
    const mq = window.matchMedia("(min-width: 641px)");
    const onChange = (e) => e.matches && setMenuOpen(false);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  // Reading-progress line under the header; one rAF-throttled scroll listener writes a CSS variable.
  const progressRef = useRef(null);
  useEffect(() => {
    let raf = 0;
    const update = () => {
      raf = 0;
      const max = document.documentElement.scrollHeight - window.innerHeight;
      const p = max > 0 ? Math.min(window.scrollY / max, 1) : 0;
      if (progressRef.current) progressRef.current.style.setProperty("--p", p.toFixed(4));
    };
    const onScroll = () => {
      if (!raf) raf = requestAnimationFrame(update);
    };
    update();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      if (raf) cancelAnimationFrame(raf);
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, []);

  const nextTheme = theme === "dark" ? "light" : "dark";

  return (
    <header className="header">
      <span className="header__progress" ref={progressRef} aria-hidden="true" />
      <div className="header__bar container">
        <a href="#top" className="header__brand">
          <span className="header__mark" aria-hidden="true">~/</span>
          {profile.name.toUpperCase()}
          <span className="header__tagline">{profile.role}</span>
        </a>

        <nav className="header__nav" id="site-nav" aria-label="Primary" data-open={menuOpen}>
          <ul className="header__list">
            {nav.map((item) => (
              <li key={item.id}>
                <a
                  href={`#${item.id}`}
                  className="header__link"
                  aria-current={active === item.id ? "location" : undefined}
                  onClick={() => setMenuOpen(false)}
                >
                  {item.label}
                </a>
              </li>
            ))}
          </ul>
          <p className="header__meta">
            <a href={`mailto:${profile.email}`}>{profile.email}</a>
            <span>{profile.location}</span>
          </p>
        </nav>

        <div className="header__actions">
          <button type="button" className="header__search" onClick={onOpenPalette}>
            <Icon name="search" />
            <span className="header__search-label">Search</span>
            <kbd aria-hidden="true">{isMac ? "⌘" : "Ctrl"} K</kbd>
            <span className="sr-only">Open command palette</span>
          </button>
          <button
            type="button"
            className="header__icon-btn"
            onClick={onToggleTheme}
            aria-label={`Switch to ${nextTheme} theme`}
          >
            <Icon name={theme === "dark" ? "sun" : "moon"} />
          </button>
          <button
            type="button"
            className="header__icon-btn header__menu-btn"
            onClick={() => setMenuOpen((v) => !v)}
            aria-expanded={menuOpen}
            aria-controls="site-nav"
            aria-label={menuOpen ? "Close menu" : "Open menu"}
          >
            <Icon name={menuOpen ? "close" : "menu"} />
          </button>
        </div>
      </div>
    </header>
  );
};

export default Header;
